import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { followTeam, unfollowTeam, fetchFollowedTeams } from "../../../features/coach/coachProfileSlice";
import { CollegeDropdown } from "./components/CollegeDropdown";
import { AllCoachesModal } from "../../../components/common/AllCoachesModal";
import { TeamLogo } from "../../player/team/PlayerTeamHeader";
import { CARD, CardTitle, InfoGrid } from "../../dashboard/profile/profileCard";
import { formatLocation } from "../../../utils/formatLocation";

// Current streak as "W4" / "L4" / "T1". Presto may send it as "W4", "W 4",
// "Won 4" or "L-2"; anything else is shown as-is.
const formatStreak = (streak) => {
  if (streak === null || streak === undefined || streak === '') return null;
  const match = String(streak).trim().match(/^(W|L|T)[a-z]*[\s-]*(\d+)$/i);
  return match ? `${match[1].toUpperCase()}${match[2]}` : String(streak);
};

const StreakValue = ({ streak }) => {
  const label = formatStreak(streak);
  if (!label) return null;
  const tone = label.startsWith('W')
    ? 'text-green-600 dark:text-green-400'
    : label.startsWith('L')
      ? 'text-red-600 dark:text-red-400'
      : 'text-jp-ink';
  return <span className={`font-extrabold tabular-nums ${tone}`}>{label}</span>;
};

export const CoachTeamHeader = ({
  selectedCollege,
  colleges,
  onSelectCollege,
  enableNavigation = false,
  onSearch,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  loading = false,
  onDropdownOpen
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showCoachesModal, setShowCoachesModal] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);

  // Get coach from coachInfo array - prioritize Head Coach
  const coachInfo = selectedCollege?.coachInfo || [];
  // Find head coach (title contains "Head"), otherwise use first coach
  const headCoach = coachInfo.find(coach =>
    coach.title?.toLowerCase().includes('head')
  );
  const firstCoach = headCoach || coachInfo[0] || null;
  const hasMultipleCoaches = coachInfo.length > 1;

  // Use first coach data or fallback to legacy fields
  const displayCoachName = firstCoach?.name || selectedCollege?.coachName;
  const displayCoachTitle = firstCoach?.title || selectedCollege?.coachPosition;
  const displayCoachPhone = firstCoach?.phone || selectedCollege?.coachPhoneNumber;
  const displayCoachEmail = firstCoach?.email || selectedCollege?.coachEmail;

  // Get followed teams from Redux
  const { followedTeams } = useSelector((state) => state.coachProfile);

  // Fetch followed teams on mount
  useEffect(() => {
    dispatch(fetchFollowedTeams({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Check if current team is in followed teams list
  useEffect(() => {
    if (selectedCollege?._id && followedTeams && followedTeams.length > 0) {
      const isTeamFollowed = followedTeams.some(
        (team) => team._id === selectedCollege._id || team.id === selectedCollege._id
      );
      setIsFollowing(isTeamFollowed);
    } else {
      setIsFollowing(false);
    }
  }, [selectedCollege, followedTeams]);

  const handleLogoClick = () => {
    if (enableNavigation && selectedCollege?._id) {
      navigate(`/coach/team/${selectedCollege._id}`, {
        state: {
          teamData: selectedCollege
        }
      });
    }
  };

  const handleFollowToggle = async () => {
    if (!selectedCollege?._id || followLoading) return;

    setFollowLoading(true);
    try {
      if (isFollowing) {
        await dispatch(unfollowTeam(selectedCollege._id)).unwrap();
        setIsFollowing(false);
      } else {
        await dispatch(followTeam(selectedCollege._id)).unwrap();
        setIsFollowing(true);
      }
      // Refresh the followed teams list to keep it in sync
      dispatch(fetchFollowedTeams({ page: 1, limit: 100 }));
    } catch (error) {
      console.error('Failed to toggle team follow:', error);
      // Optionally show error message to user
    } finally {
      setFollowLoading(false);
    }
  };

  const location = formatLocation(
    selectedCollege?.teamLocation
    || selectedCollege?.location
    || (selectedCollege?.town && selectedCollege?.state ? `${selectedCollege.town}, ${selectedCollege.state}` : null)
    || selectedCollege?.town
    || selectedCollege?.state);
  const division = selectedCollege?.divisionName || selectedCollege?.team?.divisionName || selectedCollege?.division;
  const overallRecord =
    selectedCollege?.record
    || selectedCollege?.overallRecord
    || (selectedCollege?.wins != null && selectedCollege?.losses != null
        ? `${selectedCollege.wins}-${selectedCollege.losses}${selectedCollege.ties ? `-${selectedCollege.ties}` : ''}`
        : null);
  const subtitle = [division, selectedCollege?.conference, location].filter(Boolean).join(" · ");
  const contactLink = "flex items-center gap-2 text-slate-300 hover:text-white transition-colors";

  return (
    <div className="space-y-6">
      {/* Navy header, laid out like the homepage team preview */}
      <section className="bg-jp-band rounded-3xl px-5 sm:px-8 py-6 sm:py-8 text-left">
        <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6">
          <div className="flex items-center gap-5 min-w-0">
            <TeamLogo name={selectedCollege?.name} logo={selectedCollege?.logo} />
            <div className="min-w-0">
              <p className="text-yellow-300 text-sm font-bold uppercase tracking-widest mb-2">Team Profile</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-anton leading-[1.1] tracking-tight text-white uppercase break-words">
                {selectedCollege?.name || "Team"}
              </h1>
              {subtitle && <p className="text-slate-300 text-sm sm:text-base mt-2">{subtitle}</p>}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row xl:flex-col items-start sm:items-center xl:items-end gap-3 flex-shrink-0">
            {onSearch && (
              <CollegeDropdown
                selectedCollege={selectedCollege}
                colleges={colleges}
                onSelectCollege={onSelectCollege}
                onSearch={onSearch}
                onLoadMore={onLoadMore}
                hasMore={hasMore}
                loadingMore={loadingMore}
                loading={loading}
                onDropdownOpen={onDropdownOpen}
              />
            )}
            <button
              onClick={handleFollowToggle}
              disabled={followLoading}
              className={`${
                isFollowing
                  ? 'bg-white/10 border border-white/25 hover:bg-white/20'
                  : 'bg-cyan-500 hover:bg-cyan-600'
              } text-white px-6 py-2.5 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
            >
              {followLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{isFollowing ? 'Unfollowing...' : 'Following...'}</span>
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {isFollowing ? (
                      // Check icon for followed
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    ) : (
                      // Plus icon for not followed
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    )}
                  </svg>
                  <span>{isFollowing ? 'Unfollow Team' : 'Follow Team'}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Coach contact info */}
        {(displayCoachName || displayCoachPhone || displayCoachEmail) && (
          <div className="mt-6 pt-5 border-t border-white/15 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm">
            {displayCoachName && (
              <div className="flex items-center gap-2 text-white">
                <svg className="w-4 h-4 flex-shrink-0 text-yellow-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <span className="font-semibold">{displayCoachName}</span>
              </div>
            )}
            {displayCoachPhone && (
              <a href={`tel:${displayCoachPhone}`} className={contactLink}>
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>{displayCoachPhone}</span>
              </a>
            )}
            {displayCoachEmail && (
              <a href={`mailto:${displayCoachEmail}`} className={contactLink}>
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>{displayCoachEmail}</span>
              </a>
            )}
            {/* View All Coaches Button */}
            {hasMultipleCoaches && (
              <button
                onClick={() => setShowCoachesModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/15 text-white rounded-full hover:bg-white/20 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                View All Coaches ({coachInfo.length})
              </button>
            )}
          </div>
        )}
      </section>

      <section className={`${CARD} text-left`}>
        <CardTitle title="Team Information" />
        <InfoGrid
          items={[
            { label: 'Division', value: division },
            { label: 'Region', value: selectedCollege?.region },
            { label: 'Conference', value: selectedCollege?.conference },
            { label: 'Location', value: location },
            { label: 'Rank', value: selectedCollege?.rank },
            { label: 'Overall Record', value: overallRecord },
            { label: 'Streak', value: formatStreak(selectedCollege?.streak ?? selectedCollege?.currentStreak) && <StreakValue streak={selectedCollege?.streak ?? selectedCollege?.currentStreak} /> },
            { label: 'Home', value: selectedCollege?.homeRecord || selectedCollege?.home },
            { label: 'Away', value: selectedCollege?.awayRecord || selectedCollege?.away },
            { label: 'Neutral', value: selectedCollege?.neutralRecord || selectedCollege?.neutral },
            { label: 'Conference Record', value: selectedCollege?.conferenceRecord },
          ]}
        />
      </section>

      {/* All Coaches Modal */}
      <AllCoachesModal
        isOpen={showCoachesModal}
        onClose={() => setShowCoachesModal(false)}
        coaches={coachInfo}
        teamName={selectedCollege?.name || selectedCollege?.collegeName}
      />
    </div>
  );
};
